// @flow
import addResourceAnnotations from 'app/packages/trace-logger/utils/addResourceAnnotations';
import addStopwatchToDebugPerfLogger from 'app/packages/trace-logger/utils/addStopwatchToDebugPerfLogger';
import getWindowSize from 'app/packages/trace-logger/utils/getWindowSize';
import randomTraceId from 'app/packages/trace-logger/utils/generateTraceId';
import Stopwatch from 'app/packages/trace-logger/Stopwatch';
import type { PerformanceResourceTimingV2 } from 'app/packages/pinterest-flow-types/performanceTiming';
import { DEBUG_TRACING } from 'app/packages/trace-logger/debugFlags';
import { getEntries } from 'app/packages/trace-logger/resourceTiming';

export type ContextType = {
  appVersion: string,
  browserName: string,
  browserVersion: string,
  isAuthenticated: boolean,
  isBot?: boolean,
  isMobile: boolean,
  locale: string,
  osName: string,
  stageName?: string,
  userId?: string,
};

type ResourceStats = {|
  count: number,
  nextHopProtocol: string,
  decodedBodySize: number,
|};

const bytesToKilobytes = (bytes: number): number => Number(Number(bytes / 1024).toFixed(3));

const getResourceStats = (entries: Array<PerformanceResourceTimingV2>): ResourceStats => {
  const protocols = entries.reduce((acc, e) => {
    if (e.nextHopProtocol) {
      acc[e.nextHopProtocol] ? (acc[e.nextHopProtocol] += 1) : (acc[e.nextHopProtocol] = 1);
    }
    return acc;
  }, {});
  let protocol = '';

  if (protocols['http/1.1']) {
    if (protocols.h2) {
      protocol = 'mixed';
    } else {
      protocol = 'h1';
    }
  } else if (protocols.h2) {
    protocol = 'h2';
  }

  return {
    count: entries.length,
    nextHopProtocol: protocol,
    decodedBodySize: entries.reduce((sum, e) => sum + bytesToKilobytes(e.decodedBodySize || 0), 0),
  };
};

const getVisuallyCompleteSubStopwatch = (timer: Stopwatch, metricName: string) => {
  // If we have any image subspans, create a "images_visually_complete" subspan
  const imageStopwatches = (timer.subStopwatchQueue || []).filter(({ name }) => name === 'image');
  return imageStopwatches.length
    ? new Stopwatch({
        name: 'images_visually_complete',
        metricName,
        parent: timer,
        startTime: Math.min(...imageStopwatches.map(({ startTime }) => startTime)),
        endTime: Math.max(...imageStopwatches.map(({ endTime }) => endTime || 0)),
      })
    : null;
};

const getPageLoadResourceSubStopwatches = (timer: Stopwatch, metricName: string) => {
  const resourceEntries = getEntries();
  const scriptEntries = resourceEntries.filter((e) => e.initiatorType === 'script');
  const linkEntries = resourceEntries.filter((e) => e.initiatorType === 'link');

  return [...scriptEntries, ...linkEntries].filter((entry) => entry.responseEnd).map((entry) => {
    const entryStopwatch = new Stopwatch({
      name: entry.initiatorType,
      metricName,
      parent: timer,
      startTime: window.performance.timing.navigationStart + entry.startTime,
      endTime: window.performance.timing.navigationStart + entry.responseEnd,
    });
    addResourceAnnotations(entryStopwatch, entry);
    return entryStopwatch;
  });
};

const getPageLoadEntryChunkAnnotations = (
  entry: PerformanceResourceTimingV2,
): Array<{| label: string, timestamp: number |}> =>
  [
    // see https://www.w3.org/TR/resource-timing-2/#processing-model
    { attribute: 'startTime', value: entry.startTime || 0 },
    { attribute: 'fetchStart', value: entry.fetchStart || 0 },
    { attribute: 'domainLookupStart', value: entry.domainLookupStart || 0 },
    { attribute: 'domainLookupEnd', value: entry.domainLookupEnd || 0 },
    { attribute: 'connectStart', value: entry.connectStart || 0 },
    { attribute: 'connectEnd', value: entry.connectEnd || 0 },
    { attribute: 'requestStart', value: entry.requestStart || 0 },
    { attribute: 'responseStart', value: entry.responseStart || 0 },
    { attribute: 'responseEnd', value: entry.responseEnd || 0 },
  ]
    .filter(({ value }) => value > 0)
    .map(({ attribute, value }) => ({
      label: `EntryChunk_${attribute}`,
      timestamp: window.performance.timing.navigationStart + value,
    }));

const getResourceBufferAnnotations = (
  isPageLoad: boolean,
  pwtStartTime: number,
): Array<{| label: string, timestamp: number |}> =>
  window.performance
    .getEntriesByType('mark')
    .filter((e) => e.name.startsWith('resourceBuffer'))
    .map(({ name, startTime }) => ({
      label: name,
      timestamp:
        (isPageLoad ? window.performance.timing.navigationStart : pwtStartTime) + startTime,
    }));

const getDeferredScriptsBinaryAnnotations = () => {
  const deferredScriptUrls: Array<string> = Array.from(
    document.querySelectorAll('head > script[defer]'),
  )
    .map((el) => (el instanceof HTMLScriptElement && el.src ? el.src : ''))
    .filter(Boolean);

  const annotations = [
    {
      name: 'deferredScripts.count',
      value: deferredScriptUrls.length,
      annotation_type: 'I16',
    },
  ];
  if (!deferredScriptUrls.length) {
    return annotations;
  }
  const {
    transferSizeTotal,
    transferSizeEmptyCount,
    serviceWorkerCount,
    slowFetchStartCount,
    deferredScriptsWithTimingCount,
  } = getEntries()
    .filter((e) => e.initiatorType === 'script' && deferredScriptUrls.includes(e.name))
    .reduce(
      (
        {
          transferSizeTotal,
          transferSizeEmptyCount,
          serviceWorkerCount,
          slowFetchStartCount,
          deferredScriptsWithTimingCount,
        },
        e,
      ) => ({
        transferSizeTotal: transferSizeTotal + e.transferSize,
        transferSizeEmptyCount: transferSizeEmptyCount + (e.transferSize === 0 ? 1 : 0),
        serviceWorkerCount: serviceWorkerCount + (!!e.workerStart ? 1 : 0),
        slowFetchStartCount: slowFetchStartCount + (e.fetchStart - e.startTime > 100 ? 1 : 0),
        deferredScriptsWithTimingCount: deferredScriptsWithTimingCount + 1,
      }),
      {
        transferSizeTotal: 0,
        transferSizeEmptyCount: 0,
        serviceWorkerCount: 0,
        slowFetchStartCount: 0,
        deferredScriptsWithTimingCount: 0,
      },
    );
  return annotations.concat([
    {
      name: 'deferredScripts.transferSizeTotal',
      value: transferSizeTotal,
      annotation_type: 'I32',
    },
    {
      name: 'deferredScripts.transferSizeEmptyCount',
      value: transferSizeEmptyCount,
      annotation_type: 'I16',
    },
    {
      name: 'deferredScripts.serviceWorkerCount',
      value: serviceWorkerCount,
      annotation_type: 'I16',
    },
    {
      name: 'deferredScripts.slowFetchStartCount',
      value: slowFetchStartCount,
      annotation_type: 'I16',
    },
    {
      name: 'deferredScripts.withTimingCount',
      value: deferredScriptsWithTimingCount,
      annotation_type: 'I16',
    },
  ]);
};

export default class TraceEventHandler {
  traceName: string;
  cause: ?number;
  experimentMap: { [name: string]: string };
  metricName: string;
  perfMarkStartName: string;
  sampleRate: number;
  state: Object;
  viewType: ?number;
  viewParameter: ?number;
  _ajax: Function;
  _context: ContextType;
  _currTimer: ?Stopwatch;
  _currTraceId: ?string;
  traceLoggerVersion: string;
  routeEvent: (traceEvent: Object) => boolean;
  cleanupCallbacks: Array<() => void>;

  constructor(metricName: string, ajax: Function, context: ContextType) {
    this._ajax = ajax;
    this._context = context;
    this.traceName = '';
    this.metricName = metricName;
    this.perfMarkStartName = '';
    this.sampleRate = 1;
    this.viewType = null;
    this.viewParameter = null;
    this.cause = null;
    this.state = {};
    this._currTimer = null;
    this._currTraceId = null;
    this.experimentMap = {};
    this.traceLoggerVersion = 'none'; // should be overriden by subclasses
    this.cleanupCallbacks = [];
    this.routeEvent = () => false;
  }

  hasActiveStopwatch() {
    return !!this._currTimer;
  }

  getCurrentTraceId() {
    return this._currTraceId;
  }

  getCurrentStopwatch() {
    return this._currTimer;
  }

  _pwtSubStopwatches(timer: Stopwatch) {
    const isPageLoad = this.cause === PCONST.thrift.UpwtMetricCause.WEB_PAGE_LOAD;
    const visuallyCompleteSubStopwatch = getVisuallyCompleteSubStopwatch(timer, this.metricName);
    return [
      ...(isPageLoad ? getPageLoadResourceSubStopwatches(timer, this.metricName) : []),
      ...(visuallyCompleteSubStopwatch ? [visuallyCompleteSubStopwatch] : []),
    ];
  }

  _pwtAnnotations(timer: Stopwatch, entryChunkResourceEntry?: PerformanceResourceTimingV2) {
    const isPageLoad = this.cause === PCONST.thrift.UpwtMetricCause.WEB_PAGE_LOAD;
    const resourceBufferAnnotations =
      getResourceBufferAnnotations(isPageLoad, timer.startTime) || [];
    const pageLoadEntryChunkAnnotations = entryChunkResourceEntry
      ? getPageLoadEntryChunkAnnotations(entryChunkResourceEntry)
      : [];
    return [...resourceBufferAnnotations, ...pageLoadEntryChunkAnnotations];
  }

  _pwtBinaryAnnotations(
    { annotations, binaryAnnotations, result, resultReason, endTime }: Stopwatch,
    entryChunkResourceEntry?: PerformanceResourceTimingV2,
  ) {
    // All the PWT-required binary annotations
    const resourceEntriesMap = getEntries().reduce(
      (map, e) => {
        if (
          e.initiatorType === 'script' ||
          e.initiatorType === 'link' ||
          e.initiatorType === 'img' ||
          e.initiatorType === 'video'
        ) {
          return { ...map, [e.initiatorType]: map[e.initiatorType].concat(e) };
        }
        return map;
      },
      { script: [], link: [], img: [], video: [] },
    );
    const resourceStats: {|
      script: ResourceStats,
      link: ResourceStats,
      img: ResourceStats,
      video: ResourceStats,
    |} = {
      script: getResourceStats(resourceEntriesMap.script),
      link: getResourceStats(resourceEntriesMap.link),
      img: getResourceStats(resourceEntriesMap.img),
      video: getResourceStats(resourceEntriesMap.video),
    };

    const serviceWorker = window.navigator.serviceWorker;
    const experimentMap = this.experimentMap;

    return [
      {
        name: 'locale',
        value: this._context.locale,
        annotation_type: 'STRING',
      },
      {
        name: 'pwtActionName',
        value: this.metricName,
        annotation_type: 'I16',
      },
      {
        name: 'userAgent',
        value: window.navigator.userAgent,
        annotation_type: 'STRING',
      },
      {
        name: 'app.type',
        value: this._context.isMobile ? 6 : 5,
        annotation_type: 'I16',
      },
      {
        name: 'app.version',
        value: this._context.appVersion,
        annotation_type: 'STRING',
      },
      {
        name: 'device.type',
        value: 0,
        annotation_type: 'I16',
      },
      {
        name: 'device.version',
        value: 'unknown',
        annotation_type: 'STRING',
      },
      {
        name: 'devicePixelRatio',
        value: window.devicePixelRatio ? Math.round(window.devicePixelRatio) : 0,
        annotation_type: 'I32',
      },
      {
        name: 'browser.name',
        value: this._context.browserName,
        annotation_type: 'STRING',
      },
      {
        name: 'browser.version',
        value: this._context.browserVersion,
        annotation_type: 'STRING',
      },
      {
        name: 'osName',
        value: this._context.osName,
        annotation_type: 'STRING',
      },
      {
        name: 'stageName',
        value: this._context.stageName,
        annotation_type: 'STRING',
      },
      {
        name: 'view.type',
        value: this.viewType,
        annotation_type: 'I32',
      },
      {
        name: 'pwt.cause',
        value: this.cause,
        annotation_type: 'I16',
      },
      {
        name: 'pwt.result',
        value: result,
        annotation_type: 'I16',
      },
      {
        name: 'view.parameter',
        value: this.viewParameter,
        annotation_type: 'I32',
      },
      ...Object.keys(experimentMap).map((experimentName) => ({
        name: `experiment.${experimentName}`,
        value: experimentMap[experimentName],
        annotation_type: 'STRING',
      })),
      {
        name: 'WindowSizeHXW',
        value: `${window.innerHeight}x${window.innerWidth}`,
        annotation_type: 'STRING',
      },
      {
        name: 'windowSizeCategory',
        value: getWindowSize(),
        annotation_type: 'STRING',
      },
      {
        name: 'traceLoggerVersion',
        value: this.traceLoggerVersion,
        annotation_type: 'STRING',
      },
      ...(annotations.GridBottomSpinnerStartTraceEvent && endTime
        ? [
            {
              name: 'spinner',
              value: annotations.GridBottomSpinnerStartTraceEvent.timestamp < endTime,
              annotation_type: 'BOOL',
            },
          ]
        : []),
      ...(binaryAnnotations.some(({ name }) => name === 'NewAppVersionDetected')
        ? []
        : [{ name: 'NewAppVersionDetected', value: false, annotation_type: 'BOOL' }]),
      ...(typeof this._context.isBot === 'boolean'
        ? [
            {
              name: 'isBot',
              value: this._context.isBot,
              annotation_type: 'BOOL',
            },
          ]
        : []),
      ...(entryChunkResourceEntry
        ? [
            {
              name: 'entryChunkTransferSizeEmpty',
              value: entryChunkResourceEntry.transferSize === 0,
              annotation_type: 'BOOL',
            },
            {
              name: 'entryChunkServiceWorkerUsed',
              value: !!entryChunkResourceEntry.workerStart,
              annotation_type: 'BOOL',
            },
            ...(!!entryChunkResourceEntry.fetchStart && !!entryChunkResourceEntry.startTime
              ? [
                  {
                    name: 'entryChunkSlowFetchStart',
                    value:
                      entryChunkResourceEntry.fetchStart - entryChunkResourceEntry.startTime > 100,
                    annotation_type: 'BOOL',
                  },
                ]
              : []),
            ...(entryChunkResourceEntry.responseEnd
              ? [
                  {
                    name: 'entryChunkAfterDoc',
                    value:
                      entryChunkResourceEntry.responseEnd +
                        window.performance.timing.navigationStart >
                      window.performance.timing.domInteractive,
                    annotation_type: 'BOOL',
                  },
                ]
              : []),
          ]
        : []),
      ...(resultReason
        ? [
            {
              name: 'pwt.resultReason',
              value: resultReason,
              annotation_type: 'STRING',
            },
          ]
        : []),
      ...(window.navigator.hardwareConcurrency
        ? [
            {
              name: 'cpu.speed',
              value: Math.round(window.navigator.hardwareConcurrency),
              annotation_type: 'I16',
            },
          ]
        : []),
      ...(window.navigator.deviceMemory
        ? [
            {
              name: 'device.memory',
              value: Math.round(window.navigator.deviceMemory),
              annotation_type: 'I16',
            },
          ]
        : []),
      ...(window.navigator.platform
        ? [
            {
              name: 'platform',
              value: window.navigator.platform,
              annotation_type: 'STRING',
            },
          ]
        : []),
      ...(serviceWorker
        ? [
            {
              name: 'serviceWorker',
              value: !!serviceWorker.controller,
              annotation_type: 'BOOL',
            },
          ]
        : []),
      ...(serviceWorker && serviceWorker.controller && serviceWorker.controller.state
        ? [
            {
              name: 'serviceWorkerState',
              value: serviceWorker.controller.state,
              annotation_type: 'STRING',
            },
          ]
        : []),
      ...(window.navigator.connection && window.navigator.connection.downlink
        ? [
            {
              name: 'net.speed',
              value: Math.round(window.navigator.connection.downlink * 1024), // Mbps => kbps. See https://developer.mozilla.org/en-US/docs/Web/API/NetworkInformation/downlink
              annotation_type: 'I32',
            },
          ]
        : []),
      ...(window.navigator.connection && window.navigator.connection.effectiveType
        ? [
            {
              name: 'net.effectiveType',
              value: window.navigator.connection.effectiveType,
              annotation_type: 'STRING',
            },
          ]
        : []),
      ...(window.navigator.connection && window.navigator.connection.rtt
        ? [
            {
              name: 'net.rtt',
              value: Math.round(window.navigator.connection.rtt / 10) * 10,
              annotation_type: 'I32',
            },
          ]
        : []),
      ...getDeferredScriptsBinaryAnnotations(),
      ...(window.performance
        ? [
            {
              name: 'resourceBufferFullCount',
              value: window.performance
                .getEntriesByType('mark')
                .filter((e) => e.name === 'resourceBufferFull').length,
              annotation_type: 'I16',
            },
            {
              name: 'resourceBufferClearedCount',
              value: window.performance
                .getEntriesByType('mark')
                .filter((e) => e.name === 'resourceBufferCleared').length,
              annotation_type: 'I16',
            },
          ]
        : []),
      ...['script', 'link', 'img', 'video'].reduce(
        (arr, initiatorType) =>
          arr.concat([
            {
              name: `resource.${initiatorType}.count`,
              value: resourceStats[initiatorType].count,
              annotation_type: 'I32',
            },
            {
              name: `resource.${initiatorType}.decodedBodySize`,
              value: resourceStats[initiatorType].decodedBodySize,
              annotation_type: 'I32',
            },
            {
              name: `resource.${initiatorType}.nextHopProtocol`,
              value: resourceStats[initiatorType].nextHopProtocol,
              annotation_type: 'STRING',
            },
          ]),
        [],
      ),
    ];
  }

  handleStartCurrTimer(traceEvent: Object) {
    const timer = new Stopwatch({
      name: traceEvent.name,
      metricName: this.metricName,
      startTime: Date.now(),
    });
    addStopwatchToDebugPerfLogger(timer);
    this._currTimer = timer;
    this._currTraceId = randomTraceId();

    if (DEBUG_TRACING) {
      // Mark the start time so we can measure it later using the Performance API
      this.perfMarkStartName = `${timer.uniqueName}_${timer.spanId}-start`;
      window.performance.mark(this.perfMarkStartName);
    }

    if (this.sampleRate !== 1) {
      this.state.isSampled = Math.random() < this.sampleRate;
    }
  }

  getIsSampled() {
    return DEBUG_TRACING || this.sampleRate === 1 || !!this.state.isSampled;
  }

  cleanUpHandler() {
    (this.cleanupCallbacks || []).forEach((cleanup) => cleanup());
    this._currTimer = null;
    this._currTraceId = null;
    this.state = {};
  }

  sendPWT = () => {
    // At this point, all items in the stopwatch queue should be finished and we are able to send
    // a payload out.
    //
    // This is the point where we assemble all the data for a zipkin trace.
    // We've been using the events and handlers themselves to inform a hierarchy
    // via their APIs, so the individual send event call sites are unaware of the
    // larger trees that they are forming. Each handler will have their own way of constructing
    // their individual tree (1 handler = 1 tree) by putting together the combined data of the
    // _currTimer and its submetrics (1 timer = 1 span)
    //
    // TODO(jchan): Generalize this for all metrics. We should batch these and send them out every X seconds.
    // console.log('timer has stopped!', this._currTimer);
    const timer = this._currTimer;
    if (!timer) {
      return;
    }

    if (this.getIsSampled()) {
      const entryChunkResourceEntry =
        this.cause === PCONST.thrift.UpwtMetricCause.WEB_PAGE_LOAD
          ? getEntries().find(
              (e) => e.initiatorType === 'script' && e.name.includes('entryChunk-www'),
            )
          : undefined;
      this._pwtSubStopwatches(timer).forEach((watch) => {
        timer.addSubStopwatch(watch);
      });
      this._pwtAnnotations(timer, entryChunkResourceEntry).forEach(({ timestamp, label }) => {
        timer.addAnnotation(label, timestamp);
      });
      this._pwtBinaryAnnotations(timer, entryChunkResourceEntry).forEach((binaryAnnotation) => {
        timer.addBinaryAnnotation(binaryAnnotation);
      });

      const traceId = this._currTraceId;
      if (traceId) {
        const rootSpanName = this.traceName;
        const spans = timer.getSpans(rootSpanName);
        if (spans) {
          this._ajax({
            url: '/_/_/trace/trace/',
            type: 'POST',
            data: {
              report_data: JSON.stringify({
                trace_id: traceId,
                spans,
              }),
              report_context: JSON.stringify({
                debugTrace: DEBUG_TRACING && !timer.parent,
                locale: this._context.locale,
                stageName: this._context.stageName,
                userId: this._context.isAuthenticated ? this._context.userId : null,
              }),
            },
          });
          if (DEBUG_TRACING) {
            // eslint-disable-next-line no-console
            console.log(
              `Perf log reported for ${rootSpanName}. See the PinTrace here: ` +
                `https://pintrace.pinadmin.com/zipkin/traces/${traceId}`,
            );
          }
        }
      }
    }
    this.cleanUpHandler();
  };
}
